/* eslint-disable no-console */
import { getTracerModules } from '@apptracer/sdk/lite';
import type { Client } from '@stomp/stompjs';

import { packageName } from '../../../config';

interface WebSocketConnection {
  id: string;
  client: Client | null;
  timestamp: number;
}

class WebSocketConnectionManager {
  private connections: Map<string, WebSocketConnection> = new Map();
  private currentConnectionId: string | null = null;
  private connectionCounter = 0;

  /**
   * Генерирует уникальный ID для WebSocket соединения
   */
  private generateConnectionId(): string {
    this.connectionCounter = this.connectionCounter + 1;
    return `ws-${this.connectionCounter}`;
  }

  /**
   * Регистрирует новое WebSocket соединение
   */
  registerConnection(): void {
    const connectionId = this.generateConnectionId();

    this.connections.set(connectionId, {
      id: connectionId,
      client: null,
      timestamp: Date.now(),
    });

    if (this.currentConnectionId && this.currentConnectionId !== connectionId) {
      void this.closeOldConnections(connectionId);
    }

    this.currentConnectionId = connectionId;
  }

  /**
   * Устанавливает клиент для текущего активного соединения
   */
  setCurrentClient(client: Client): void {
    if (this.currentConnectionId) {
      const connection = this.connections.get(this.currentConnectionId);
      if (connection) {
        connection.client = client;
      }
    }
  }

  /**
   * Закрывает текущее активное соединение
   */
  async closeCurrentConnection(): Promise<void> {
    if (this.currentConnectionId) {
      await this.closeConnection(this.currentConnectionId);
    }
  }

  /**
   * Закрывает все соединения кроме указанного
   */
  private async closeOldConnections(exceptConnectionId: string): Promise<void> {
    const connectionsToClose: string[] = [];

    this.connections.forEach((_, id) => {
      if (id !== exceptConnectionId) {
        connectionsToClose.push(id);
      }
    });

    const closePromises = connectionsToClose.map((id) => this.closeConnection(id));
    await Promise.all(closePromises);
  }

  /**
   * Закрывает конкретное соединение
   */
  async closeConnection(connectionId: string): Promise<void> {
    const connection = this.connections.get(connectionId);
    const tracerError = getTracerModules(packageName).error;

    if (connection) {
      if (connection.client) {
        try {
          if (connection.client.active) {
            await connection.client.deactivate();
          }
        } catch (_) {
          tracerError?.error(`[WebSocket Manager]: Ошибка закрытия соединения`, {
            keys: {
              connectionId,
            },
          });
        }
      }

      this.connections.delete(connectionId);
    }
  }

  /**
   * Закрывает все соединения
   */
  async closeAllConnections(): Promise<void> {
    const closePromises: Promise<void>[] = [];

    this.connections.forEach((connection) => {
      if (connection.client && connection.client.active) {
        const closePromise = connection.client.deactivate().catch((error) => {
          console.error(`[WebSocket Manager] Error closing connection ${connection.id}:`, error);
        });
        closePromises.push(closePromise);
      }
    });

    await Promise.all(closePromises);

    this.connections.clear();
    this.currentConnectionId = null;
  }
}

export const websocketConnectionManager = new WebSocketConnectionManager();
